/**
 * Address search through Google's Places API (New), called straight from the browser with a
 * referrer-restricted key. One search is a run of autocomplete requests and one details request
 * that all carry the same session token, which Google bills as a single lookup.
 *
 * Everything here is optional. With no key the address fields are the plain inputs they always
 * were, and a slow, empty or failed lookup leaves what the person typed alone.
 */

const AUTOCOMPLETE_URL = 'https://places.googleapis.com/v1/places:autocomplete'
const DETAILS_URL = 'https://places.googleapis.com/v1/places'

/** The browser key, or '' when address search is off. */
export const placesBrowserKey = (): string =>
  (process.env.NEXT_PUBLIC_GOOGLE_MAPS_BROWSER_KEY ?? '').trim()

/** A rectangle round Victoria: results from here rank first, but a New South Wales address still turns up. */
const VICTORIA_BIAS = {
  rectangle: {
    low: { latitude: -39.3, longitude: 140.9 },
    high: { latitude: -33.9, longitude: 150.1 },
  },
}

export type Suggestion = { placeId: string; main: string; secondary: string; text: string }

export type Place = {
  placeId: string
  formatted: string
  /** Unit and street, e.g. `2/18 Main Road`. */
  street: string
  suburb: string
  state: string
  postcode: string
  lat: number
  lng: number
}

export const newSessionToken = (): string => crypto.randomUUID()

type Fetcher = typeof fetch

/**
 * Google caps each service per day on the project. Past a cap it answers 429, or 403 with a quota
 * reason, and the person should never see that: the address box goes back to being a plain input
 * for a while, with one line in the console, and what they typed stays as typed. A page that has
 * been refused stops asking until the pause is over.
 */
const PAUSE_MS = 15 * 60 * 1000
let pausedUntil = 0
let warned = false

/** For tests: forget a refusal. */
export const resetPlacesRefusal = () => {
  pausedUntil = 0
  warned = false
}

const paused = () => Date.now() < pausedUntil

function noteRefusal(response: Response) {
  if (response.status !== 429 && response.status !== 403) return
  pausedUntil = Date.now() + PAUSE_MS
  if (!warned) {
    warned = true
    console.warn('Address suggestions are paused for now. Typing an address works as usual.')
  }
}

/** The caller's own abort, and a time limit, whichever comes first. */
const within = (ms: number, signal?: AbortSignal): AbortSignal =>
  signal ? AbortSignal.any([signal, AbortSignal.timeout(ms)]) : AbortSignal.timeout(ms)

type AutocompleteReply = {
  suggestions?: Array<{
    placePrediction?: {
      placeId?: string
      text?: { text?: string }
      structuredFormat?: { mainText?: { text?: string }; secondaryText?: { text?: string } }
    }
  }>
}

/** Suggestions for what has been typed so far. Anything that goes wrong is an empty list. */
export async function suggest(
  input: string,
  sessionToken: string,
  key: string,
  options: { signal?: AbortSignal; fetcher?: Fetcher } = {}
): Promise<Suggestion[]> {
  const typed = input.trim()
  if (!key || typed.length < 3 || paused()) return []
  const fetcher = options.fetcher ?? fetch
  try {
    const response = await fetcher(AUTOCOMPLETE_URL, {
      method: 'POST',
      signal: within(5_000, options.signal),
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': key,
        'X-Goog-FieldMask':
          'suggestions.placePrediction.placeId,suggestions.placePrediction.text.text,suggestions.placePrediction.structuredFormat',
      },
      body: JSON.stringify({
        input: typed,
        sessionToken,
        languageCode: 'en-AU',
        regionCode: 'AU',
        includedRegionCodes: ['au'],
        locationBias: VICTORIA_BIAS,
      }),
    })
    if (!response.ok) {
      noteRefusal(response)
      return []
    }
    const body = (await response.json()) as AutocompleteReply
    return (body.suggestions ?? []).flatMap((s) => {
      const p = s.placePrediction
      if (!p?.placeId || !p.text?.text) return []
      return [
        {
          placeId: p.placeId,
          text: p.text.text,
          main: p.structuredFormat?.mainText?.text ?? p.text.text,
          secondary: p.structuredFormat?.secondaryText?.text ?? '',
        },
      ]
    })
  } catch {
    return []
  }
}

type Component = { longText?: string; shortText?: string; types?: string[] }
type DetailsReply = {
  id?: string
  formattedAddress?: string
  addressComponents?: Component[]
  location?: { latitude?: number; longitude?: number }
}

const part = (components: Component[], type: string): Component | undefined =>
  components.find((c) => c.types?.includes(type))

/** Turns Google's details into the fields the forms hold. Null when it lacks a position or a street. */
export function toPlace(details: DetailsReply): Place | null {
  const components = details.addressComponents ?? []
  const lat = details.location?.latitude
  const lng = details.location?.longitude
  if (!details.id || typeof lat !== 'number' || typeof lng !== 'number') return null
  const number = part(components, 'street_number')?.longText ?? ''
  const route = part(components, 'route')?.longText ?? ''
  const unit = part(components, 'subpremise')?.longText ?? ''
  const house = unit && number ? `${unit}/${number}` : number
  const street = [house, route].filter(Boolean).join(' ').trim()
  const suburb =
    part(components, 'locality')?.longText ??
    part(components, 'sublocality')?.longText ??
    part(components, 'postal_town')?.longText ??
    ''
  return {
    placeId: details.id,
    formatted: details.formattedAddress ?? '',
    street,
    suburb,
    state: part(components, 'administrative_area_level_1')?.shortText ?? '',
    postcode: part(components, 'postal_code')?.longText ?? '',
    lat,
    lng,
  }
}

/** The chosen suggestion's place, or null if Google would not say. The session ends here. */
export async function placeDetails(
  placeId: string,
  sessionToken: string,
  key: string,
  options: { signal?: AbortSignal; fetcher?: Fetcher } = {}
): Promise<Place | null> {
  if (!key || paused()) return null
  const fetcher = options.fetcher ?? fetch
  try {
    const url = `${DETAILS_URL}/${encodeURIComponent(placeId)}?sessionToken=${encodeURIComponent(sessionToken)}&languageCode=en-AU&regionCode=AU`
    // Only the fields Google bills at its cheapest details tier: the id, the address and the position.
    const response = await fetcher(url, {
      signal: within(6_000, options.signal),
      headers: {
        'X-Goog-Api-Key': key,
        'X-Goog-FieldMask': 'id,formattedAddress,addressComponents,location',
      },
    })
    if (!response.ok) {
      noteRefusal(response)
      return null
    }
    return toPlace((await response.json()) as DetailsReply)
  } catch {
    return null
  }
}
